import { useQuery } from "@tanstack/react-query";
import { buyerSubmissionsApi, type BuyerSubmissionDetailDto } from "@/api/buyers";
import { EntityTabBar } from "@/components/layout/EntityTabBar";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { EmptyState, Skeleton } from "@/design-system/components/ui";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { useAuth } from "@/hooks/useAuth";
import {
  listingDisplayName,
  listingShortName,
  shortenListingName,
} from "@/lib/listingDisplay";
import { isNotFoundError, QueryErrorState } from "./queryStates";
import { SellerPrequalificationPanel } from "./SellerPrequalificationPanel";
import { SubmissionEscalationPanel } from "./SubmissionEscalationPanel";
import { SubmissionBrokerIntroTab } from "./submission/SubmissionBrokerIntroTab";
import { SubmissionDealStructureTab } from "./submission/SubmissionDealStructureTab";
import { SubmissionIntroEmailTab } from "./submission/SubmissionIntroEmailTab";
import { SubmissionOverviewTab } from "./submission/SubmissionOverviewTab";
import { SubmissionPackageTab } from "./submission/SubmissionPackageTab";
import { SubmissionPaperworkTab } from "./submission/SubmissionPaperworkTab";
import { SUBMISSION_TAB_ITEMS, type TabKey } from "./submission/submissionTabs";

// The tab table lives in `submission/submissionTabs.ts` so the extracted
// sections can import `TabKey` without cycling through this file. Re-exported
// here because the route page, the record drawer and the tests have always
// imported all three from this module, and moving where they are DEFINED should
// not move where they are FOUND.
export {
  SUBMISSION_TAB_ITEMS,
  parseTabKey,
} from "./submission/submissionTabs";
export type { TabKey } from "./submission/submissionTabs";

export interface SubmissionDetailBodyProps {
  submissionId: string;
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
}

/**
 * One submission's tabbed workspace, independent of how it was reached.
 *
 * <p>The route page and the drawer render THIS, so the two can never drift into
 * showing different things about the same submission. Tab state is the caller's:
 * a route puts it in `?tab=` so a link to "that submission's documents" is a
 * link rather than an instruction, and a drawer keeps it local because a drawer
 * is not a place.</p>
 *
 * <p>🔴 <b>ONE read serves every tab.</b> Overview, Outreach, Paperwork
 * and the Conversation log all receive the same already-fetched record rather
 * than fetching their own. The NDA fields and the milestone list project the
 * SAME columns, so a section with its own query would disagree with its sibling
 * for the length of any refetch window — showing an NDA as signed on one tab and
 * outstanding on another. Sections take the record as a prop; only the
 * cross-cutting panels, which are other modules' surfaces, read for
 * themselves.</p>
 *
 * <p>The six cross-cutting tabs are the SHARED components every other CRM record
 * uses, reached through the `BuyerSubmission` / `buyer_submission` registrations.
 * Nothing here is a submission-specific copy of Notes or Tasks.</p>
 */
export function SubmissionDetailBody({
  submissionId,
  activeTab,
  onTabChange,
}: SubmissionDetailBodyProps) {
  const { hasPermission } = useAuth();

  const submission = useQuery({
    queryKey: buyerSubmissionKeys.detail(submissionId),
    queryFn: () => buyerSubmissionsApi.get(submissionId),
    enabled: Boolean(submissionId),
  });

  if (submission.isLoading) return <Skeleton className="h-64 w-full" />;
  // Only a 404 licenses "It may have been removed" — anything else is a fact
  // about the REQUEST, and saying "removed" about a submission that exists is
  // how a specialist stops chasing a live deal.
  if (submission.isError && !isNotFoundError(submission.error)) {
    return (
      <QueryErrorState
        what="this submission"
        onRetry={() => void submission.refetch()}
        retrying={submission.isFetching}
      />
    );
  }
  if (!submission.data) {
    return <EmptyState title="Submission not found" description="It may have been removed." />;
  }

  const s = submission.data;
  // Both halves of the rule: the module permission, and the row's own
  // server-resolved ownership answer. Either one missing means every write
  // would be refused, so the affordances stay hidden rather than failing.
  //
  // 🔴 Computed ONCE, here, and threaded into every section. No extracted
  // section computes its own — three of them once gated on the row's bare
  // `canEdit` and offered writes the server refuses.
  const canWrite = s.canEdit && hasPermission("Submissions.Manage");
  // The pairing IS the submission's name — it has no other. Matches the chip
  // label the messaging resource picker mints for `buyer_submission`, so the
  // same record reads the same everywhere it is referenced.
  //
  // 🔴 SHORT, and it must stay in lockstep with the picker AND with the server
  // (ResourceLabelService / CommunicationOwnerLabelService / RelatedEntityResolver,
  // all three of which now shorten too). This label rides into chips the server
  // re-resolves on send; a one-sided cut here is the three-ladder bug again, in
  // the same record, a second apart. Applied INSIDE the pairing: shortening the
  // composed string would cut at the first separator in either half.
  const listingName = shortenListingName(s.listing?.displayName) ?? listingShortName(s.listing);
  const label = `${s.buyerDisplayName} → ${listingName}`;

  return (
    <>
      <EntityTabBar
        ariaLabel="Submission sections"
        testIdPrefix="submission"
        items={SUBMISSION_TAB_ITEMS}
        value={activeTab}
        onChange={onTabChange}
        className="mb-space-4"
      />

      {/* 🔴 No `canWrite`. Layout Standard §4.1a — an Overview may summarise,
          reference and link but may not mutate or send — so Overview has no
          write to gate, and the absent prop is the pin: a section that grows one
          has to come back through this line to be given the answer. */}
      {activeTab === "overview" && (
        <SubmissionOverviewTab s={s} onTabChange={onTabChange} />
      )}

      {/* The intros and the package, three tabs. Different recipients,
          different purposes, and different entitlement semantics — only the
          package moves `PackageSentAtUtc`. The buyer intro and the broker
          intro are two tabs since D23 (reversing K1). */}
      {/* Keyed by submission: a drawer that moves to another submission must not
          carry this one's open cards, revealed editors or copied text across. */}
      {activeTab === "intro-email" && <SubmissionIntroEmailTab key={s.id} s={s} canWrite={canWrite} />}

      {activeTab === "broker-intro" && <SubmissionBrokerIntroTab key={s.id} s={s} canWrite={canWrite} />}

      {activeTab === "package" && <SubmissionPackageTab s={s} canWrite={canWrite} />}

      {activeTab === "deal-structure" && <SubmissionDealStructureTab s={s} canWrite={canWrite} />}

      {activeTab === "paperwork" && (
        <SubmissionPaperworkTab
          s={s}
          canWrite={canWrite}
          /* The diligence checklist's Financials row has no upload target of its
             own, so it points at Documents. The prop is optional and the row
             degrades to prose naming the tab when it is absent — wiring it here
             turns that sentence into the jump it describes. */
          onOpenDocuments={() => onTabChange("documents")}
        />
      )}

      {/* 🔴 Escalation is a TAB, not a panel on Overview — Layout Standard
          §4.1a. Minting a deal is the most consequential mutation this record
          has, and an Overview may summarise and link but may not mutate.

          🔴 The seller prequalification came WITH it, and the order is the one
          it had on Overview. The adjacency is behavioural, not cosmetic: the
          escalation gate WARNS when the prequalification is missing — it never
          blocks, because a seller prequalification depends on a third party
          Phoenix cannot compel — so the control that resolves the warning has to
          be what the specialist's eye lands on next. That argument was always
          about the two being adjacent, never about them being on Overview, so it
          survives the move intact. Overview now carries a read-only summary of
          the same server answer and a link here. */}
      {activeTab === "escalation" && (
        <div className="grid gap-space-5">
          <SellerPrequalificationPanel submissionId={s.id} canEdit={canWrite} />
          <SubmissionEscalationPanel s={s} canWrite={canWrite} />
        </div>
      )}

      {/* No permission gate on the Schedule button, deliberately: all five
          Appointments.* permissions are absent from RolePermissionMap, so gating
          it the way Tasks gates its own would hide scheduling from every
          non-admin. AppointmentListMini gates on entityArchived alone, and a
          submission has no archived flag to pass. */}
      {activeTab === "calendar" && (
        <AppointmentListMini
          relatedEntityType="BuyerSubmission"
          relatedEntityId={s.id}
          entityLabel={label}
        />
      )}

      {/* 🔴 The SUBMISSION's own folder, not the buyer's and not the listing's:
          an NDA, a CIM and the financials a broker sent for THIS pairing are the
          other buyer's business in neither direction.

          `rootName` is a HINT only — EnsureEntityTree resolves the policy name
          server-side and that answer wins, so the folder is named by
          EntityRootNameResolver rather than by whatever this page happened to
          hold. It is passed for the pathological case of an owner type the
          resolver does not know, where the alternative is a 400 and no tab. */}
      {activeTab === "documents" && (
        <DocumentPanel
          ownerType="BuyerSubmission"
          ownerId={s.id}
          rootName={submissionRootNameHint(s)}
        />
      )}

      {activeTab === "tasks" && (
        <TaskListMini
          relatedEntityType="BuyerSubmission"
          relatedEntityId={s.id}
          addButtonLabel="+ Add task linked to this submission"
        />
      )}

      {activeTab === "messages" && (
        <RelatedConversationsPanel
          resourceType="buyer_submission"
          resourceId={s.id}
          entityLabel={label}
        />
      )}

      {activeTab === "communications" && (
        <CommunicationsTabPanel
          ownerType="buyer_submission"
          ownerId={s.id}
          ownerLabel={label}
        />
      )}

      {activeTab === "notes" && <NotesTabPanel ownerType="buyer_submission" ownerId={s.id} />}

      {activeTab === "activity" && (
        <ActivityTabPanel ownerType="buyer_submission" ownerId={s.id} />
      )}
    </>
  );
}

/**
 * A display name for the submission's document root, used only as a fallback.
 *
 * <p>Mirrors the server's naming policy — listing, the day the pairing was made,
 * and enough of the id to tell two submissions on the same listing apart on the
 * same day — so that if it ever IS consumed the folder is not named differently
 * from every other one. The server-resolved name takes precedence, so in the
 * ordinary case this value is never persisted.</p>
 *
 * <p>🔴 The FULL listing name, deliberately, where the header above is short.
 * `BuyerSubmissionFolderNaming` is reproduced in T-SQL by an applied migration
 * and pinned by a parity test, so it is frozen — mirroring it means mirroring
 * the whole name. A short hint would name one folder differently from every
 * other in exactly the case this fallback exists for.</p>
 */
function submissionRootNameHint(s: BuyerSubmissionDetailDto): string {
  const listingName = s.listing?.displayName?.trim() || listingDisplayName(s.listing);
  return `${listingName} — ${s.createdAt.slice(0, 10)} (${s.id.slice(0, 8)})`;
}
